import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setAuthToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null); // null = checking, false = anon, object = user
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // Skip while OAuth callback is being processed
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) return data;
    if (data.token) setAuthToken(data.token);
    setUser(data.user);
    return data;
  };

  const verifyTwoFactor = async (mfaToken, code) => {
    const { data } = await api.post("/auth/verify-2fa", { mfa_token: mfaToken, code });
    if (data.token) setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const acceptInvite = async (token, payload) => {
    const { data } = await api.post(`/invite/${token}/accept`, payload);
    if (data.token) setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    if (data.token) setAuthToken(data.token);
    setUser(data.user);
    return data.user;
  };

  const applySession = (u, token) => {
    if (token) setAuthToken(token);
    setUser(u);
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {
      /* noop */
    }
    setAuthToken(null);
    setUser(false);
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, applySession, checkAuth, verifyTwoFactor, acceptInvite }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
